import { IProjectFolder } from '../models/folder.model';
import {
  ElementKind,
  IProjectInfo,
  OneOfProjectListElements,
} from '../models/project.model';
import { ItemOrItemId } from '../models/type-helpers.model';
import { getId } from './object.helpers';
import { getElementKind } from './recursive-element.helpers';

export const isProjectInfoOrFolder = (
  element: unknown,
): element is IProjectInfo | IProjectFolder =>
  isProjectInfo(element) || isProjectFolder(element);

export const isProjectInfo = (element?: unknown): element is IProjectInfo =>
  getElementKind(element) === ElementKind.ProjectInfo;

export const isProjectFolder = (element?: unknown): element is IProjectFolder =>
  getElementKind(element) === ElementKind.ProjectFolder;

export const getProjectParentFolder = (
  list: OneOfProjectListElements[],
  item: OneOfProjectListElements,
): IProjectFolder | undefined => {
  const parentId = item.parent_id;
  const parent = parentId
    ? list.find((item) => item.id === parentId)
    : undefined;
  if (parent && !isProjectFolder(parent)) {
    throw new Error('Parent is not a folder');
  }
  return parent;
};

/**
 * Get path to project/folder based on parent_id.
 * @param list
 * @param itemOrId
 * @returns Array of parent folders starting from the root folder
 */
export const getProjectFolderPath = (
  list: OneOfProjectListElements[],
  itemOrId: ItemOrItemId<OneOfProjectListElements>,
): IProjectFolder[] => {
  const id = getId(itemOrId);
  const result: IProjectFolder[] = [];
  let current = list.find((item) => item.id === id);

  while (current) {
    if (isProjectFolder(current)) {
      // If it's already added to the result, it means we have infinite recursion (circular reference)
      if (result.includes(current)) {
        throw new Error('Infinite folder recursion');
      }
      result.unshift(current);
    }

    current = getProjectParentFolder(list, current);
  }

  return result;
};

/**
 * Make a tree of folders and projects based on parent_id.
 * Assigns inclusive descendant {@link IProjectFolder.gfa} and `updated_at` in
 * one O(n) walk.
 */
export const getProjectFolderTree = (
  items: OneOfProjectListElements[],
): OneOfProjectListElements[] => {
  const rootItems: OneOfProjectListElements[] = [];
  const projects = items.filter(
    (item) => isProjectInfo(item) && !item.template,
  );
  // Clone folders so rollups do not mutate the store/API payload in place.
  const folders: IProjectFolder[] = items
    .filter(isProjectFolder)
    .map((folder) => ({ ...folder, elements: [] }));

  [...projects, ...folders].forEach((item) => {
    const parent = getProjectParentFolder(folders, item);
    if (parent) {
      parent.elements ??= [];
      parent.elements.unshift(item);
    } else {
      rootItems.unshift(item);
    }
  });

  applyFolderRollups(rootItems);

  return rootItems;
};

/**
 * Inclusive descendant GFA and latest `updated_at` for a folder.
 */
export type FolderRollup = {
  gfa: number;
  updated_at: string;
};

/**
 * Walk a project-list tree once and write inclusive GFA/`updated_at` onto every
 * folder. Nested folders store the sum of descendant projects.
 */
export const applyFolderRollups = (
  nodes: OneOfProjectListElements[],
): FolderRollup => {
  let gfa = 0;
  let updated_at = '';

  for (const node of nodes) {
    const child = isProjectFolder(node)
      ? applyFolderNodeRollup(node)
      : getProjectInfoRollup(node);
    gfa += child.gfa;
    updated_at = maxTimestamp(updated_at, child.updated_at);
  }

  return { gfa, updated_at };
};

const applyFolderNodeRollup = (folder: IProjectFolder): FolderRollup => {
  const fromChildren = applyFolderRollups(folder.elements ?? []);
  const rollup: FolderRollup = {
    gfa: fromChildren.gfa,
    // Persisted folder.updated_at is the previous descendant max; do not use it
    // as a floor or last-updated stays on a deleted project.
    updated_at:
      fromChildren.updated_at || toComparableTimestamp(folder.updated_at),
  };
  folder.gfa = rollup.gfa;
  folder.updated_at = rollup.updated_at;
  return rollup;
};

const getProjectInfoRollup = (project: IProjectInfo): FolderRollup => ({
  gfa: project.gfa ?? 0,
  updated_at: toComparableTimestamp(project.updated_at),
});

const toComparableTimestamp = (value: string | Date | undefined): string => {
  if (!value) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  return value;
};

const maxTimestamp = (
  left: string | Date | undefined,
  right: string | Date | undefined,
): string => {
  const a = toComparableTimestamp(left);
  const b = toComparableTimestamp(right);
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};
